import { memo } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, Grid, IconButton, Tooltip } from "@mui/material"
import { faRotateLeft } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { StandardLoadingButton } from "../../../buttons/LoadingButton"
import { CreateOrderDialogFlow, CreateOrderFlowType } from "../../createOrderTypes"
import { OrderFormStatus } from "../../mainForm/helpers"
import {
    useResourceBookingDraftActions,
    useResourceBookingDraftData,
} from "./ResourceBookingDraftContext"

type StyleProps = Readonly<{
    isFixed: boolean
}>
const useStyles = makeStyles<StyleProps>()((theme, { isFixed }) => ({
    fixedContainer: {
        width: "fit-content",
        position: "fixed",
        bottom: "1.5rem",
        right: "1.5rem",
        zIndex: 1000,
        textAlign: "right",
        [theme.breakpoints.down("sm")]: {
            width: "auto",
            left: "1rem",
            right: "1rem",
            bottom: "calc(1rem + env(safe-area-inset-bottom))",
        },
    },
    cancelButton: {
        color: theme.palette.tailwind.slate[800],
        background: theme.palette.common.white,
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        borderRadius: ".75rem",
        "&:hover": {
            backgroundColor: theme.palette.tailwind.slate[100],
        },
    },
    buttonWrap: {
        background: theme.palette.common.white,
        width: isFixed ? "11.25rem" : "100%",
        borderRadius: "1rem",
        [theme.breakpoints.down("sm")]: {
            width: "100%",
        },
    },
}))

type ResourceBookingControlsProps = Readonly<{
    onBook: () => void
    isFixed?: boolean
    isLoadingButton?: boolean
    flow?: CreateOrderDialogFlow["flow"]
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
}>

export const ResourceBookingControls = memo(
    ({
        onBook,
        isFixed = true,
        isLoadingButton = false,
        flow,
        orderFormStatusRef,
    }: ResourceBookingControlsProps) => {
        const { classes, cx } = useStyles({ isFixed })
        const { draftEvent, initialEventStateId, isUpdatedOrder } = useResourceBookingDraftData()
        const { handleCancelClick } = useResourceBookingDraftActions()
        const { isFullInitOrder, resourceBookingId } = orderFormStatusRef.current
        const isResourceBooking = isFullInitOrder || !!resourceBookingId

        const isOutlookFlow = flow === CreateOrderFlowType.OUTLOOK
        const hasCurrentEvent = !!draftEvent
        const hasInitialId = !!initialEventStateId

        const showUndo =
            isOutlookFlow ? isUpdatedOrder : (!hasInitialId && hasCurrentEvent) || (hasInitialId && isUpdatedOrder)

        return (
            <Grid
                container
                flexWrap="nowrap"
                alignItems="center"
                justifyContent="flex-end"
                gap="1rem"
                className={cx(isFixed && classes.fixedContainer)}
            >
                {showUndo && (
                    <LocalizedStrict id="create-order-undo-button" attrs={{ title: true }}>
                        <Tooltip title="Undo all">
                            <IconButton className={classes.cancelButton} size="large" onClick={handleCancelClick}>
                                <FontAwesomeIcon size="sm" icon={faRotateLeft} />
                            </IconButton>
                        </Tooltip>
                    </LocalizedStrict>
                )}

                <Box className={classes.buttonWrap}>
                    <LocalizedStrict id={isResourceBooking ? "create-order-update-button" : "create-order-book-button"}>
                        <StandardLoadingButton
                            loading={isLoadingButton}
                            type="button"
                            fullWidth
                            disabled={!draftEvent?.resourceIds.length}
                            onClick={onBook}
                        >
                            Book / Update
                        </StandardLoadingButton>
                    </LocalizedStrict>
                </Box>
            </Grid>
        )
    },
)
